import { eventChannel } from "@redux-saga/core";
import { call, fork, put, take, takeEvery } from "@redux-saga/core/effects";
import { createAction, PayloadAction } from "@reduxjs/toolkit";
import { adminActions } from "../features";
import { AdminAccount, adminAPI } from "../features/admin/adminAPI";
import { fetchMessages } from "./messages";
import { pushNotification } from "./notifications";

export const checkAdminSession = createAction("saga/admin/checkSession");
function* handleCheckAdminSession() {
  try {
    yield put(adminActions.setStatus("pending"));
    yield call(adminAPI.checkSession);
    yield put(adminActions.setIsAdminAuthenticated(true));
    yield put(adminActions.setStatus("fulfilled"));
  } catch {
    yield put(adminActions.setIsAdminAuthenticated(false));
    yield put(adminActions.setStatus("rejected"));
  }
}

const createSessionSyncChannel = (syncInEveryMs: number) =>
  eventChannel<string>((emitter) => {
    const key = setInterval(() => {
      emitter("sync");
    }, syncInEveryMs);
    return () => {
      clearInterval(key);
      emitter("END");
    };
  });
function* backgroundCheckSession() {
  const chan = createSessionSyncChannel(15000);
  while (true) {
    yield take(chan);
    yield put(checkAdminSession());
  }
}

export const adminLogout = createAction("saga/admin/logout");
function* handleAdminLogout() {
  try {
    yield put(adminActions.setStatus("pending"));
    yield call(adminAPI.logout);
    yield put(
      pushNotification({
        text: "Successfully logged out.",
        type: "success",
      })
    );
    yield put(adminActions.setIsAdminAuthenticated(false));
    yield put(adminActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(adminActions.setStatus("rejected"));
  }
}

export const adminLogin =
  createAction<Omit<AdminAccount, "id">>("saga/admin/login");
function* handleAdminLogin(action: PayloadAction<Omit<AdminAccount, "id">>) {
  try {
    yield put(adminActions.setStatus("pending"));
    yield call(adminAPI.login, action.payload);
    yield put(adminActions.setIsAdminAuthenticated(true));
    yield put(fetchMessages());

    yield put(
      pushNotification({
        text: "Successfully logged in.",
        type: "success",
      })
    );
    yield put(adminActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: `Login failed. ${error.toString()}`,
        type: "error",
      })
    );
    yield put(adminActions.setStatus("rejected"));
  }
}

export const adminRegister = createAction<Omit<AdminAccount, "id">>(
  "saga/admin/register"
);
function* handleAdminRegister(action: PayloadAction<Omit<AdminAccount, "id">>) {
  try {
    yield put(adminActions.setStatus("pending"));
    yield call(adminAPI.register, action.payload);
    yield put(
      pushNotification({
        text: "Admin account was successfully registered.",
        type: "success",
      })
    );
    yield put(adminActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: error.toString(),
        type: "error",
      })
    );
    yield put(adminActions.setStatus("rejected"));
  }
}

export function* processAdminActions() {
  yield takeEvery("saga/admin/login", handleAdminLogin);
  yield takeEvery("saga/admin/logout", handleAdminLogout);
  yield takeEvery("saga/admin/register", handleAdminRegister);
  yield takeEvery("saga/admin/checkSession", handleCheckAdminSession);
  yield fork(backgroundCheckSession);
}
